import { useState } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'];
type TransactionInsert = Database['public']['Tables']['transactions']['Insert'];
type IncomeType = Database['public']['Tables']['income_types']['Row'];
type ExpenseType = Database['public']['Tables']['expense_types']['Row'];

type Period = 'today' | 'yesterday' | 'week' | 'month' | 'all';

export function useTransactions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Active types only by default; pass true to also get deleted (inactive)
   *  ones, e.g. to label old transactions. */
  const getIncomeTypes = async (includeInactive = false) => {
    try {
      let query = supabase.from('income_types').select('*');
      if (!includeInactive) query = query.eq('is_active', true);
      const { data, error: fetchError } = await query.order('name');

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Error fetching income types:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch income types');
      return [];
    }
  };

  const createIncomeType = async (name: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: insertError } = await supabase
        .from('income_types')
        .insert({ name })
        .select()
        .single();

      if (insertError) throw insertError;
      return data;
    } catch (err) {
      console.error('Error creating income type:', err);
      setError(err instanceof Error ? err.message : 'Failed to create income type');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /** Active types only by default; pass true to also get deleted (inactive) ones. */
  const getExpenseTypes = async (includeInactive = false) => {
    try {
      let query = supabase.from('expense_types').select('*');
      if (!includeInactive) query = query.eq('is_active', true);
      const { data, error: fetchError } = await query.order('name');

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Error fetching expense types:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch expense types');
      return [];
    }
  };

  const createExpenseType = async (name: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: insertError } = await supabase
        .from('expense_types')
        .insert({ name })
        .select()
        .single();

      if (insertError) throw insertError;
      return data;
    } catch (err) {
      console.error('Error creating expense type:', err);
      setError(err instanceof Error ? err.message : 'Failed to create expense type');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Rename or (soft) delete a cash category. Deleting sets is_active=false:
   * existing transactions reference the type, so the row must stay.
   */
  const updateCashType = async (
    table: 'income_types' | 'expense_types',
    id: string,
    updates: { name?: string; is_active?: boolean }
  ) => {
    try {
      setError(null);
      const { data, error: updateError } = await supabase
        .from(table)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      return data as IncomeType | ExpenseType;
    } catch (err) {
      console.error(`Error updating ${table}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to update type');
      return null;
    }
  };

  const updateIncomeType = (id: string, updates: { name?: string; is_active?: boolean }) =>
    updateCashType('income_types', id, updates) as Promise<IncomeType | null>;

  const updateExpenseType = (id: string, updates: { name?: string; is_active?: boolean }) =>
    updateCashType('expense_types', id, updates) as Promise<ExpenseType | null>;

  const getTransactions = async (period: Period = 'today') => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('transactions')
        .select(`
          *,
          income_type:income_types(*),
          expense_type:expense_types(*)
        `);

      // If period is 'all', don't add date filters
      if (period !== 'all') {
        let startDate = new Date();
        let endDate = new Date();

        switch (period) {
          case 'yesterday':
            startDate.setDate(startDate.getDate() - 1);
            endDate = new Date(startDate);
            break;
          case 'week':
            startDate.setDate(startDate.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(startDate.getMonth() - 1);
            break;
          default:
            // today - use default dates
            break;
        }

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      const { data, error: fetchError } = await query.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = async (transaction: Omit<TransactionInsert, 'id' | 'user_id' | 'created_at'>) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: insertError } = await supabase
        .from('transactions')
        .insert(transaction)
        .select()
        .single();

      if (insertError) throw insertError;
      return data;
    } catch (err) {
      console.error('Error adding transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to add transaction');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getSummary = async (period: Period = 'today') => {
    try {
      const transactions = await getTransactions(period);

      // Calculate cash totals
      const cashIncome = transactions
        .filter(t => t.type === 'income' && t.payment_method === 'cash')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const cashExpenses = transactions
        .filter(t => t.type === 'expense' && t.payment_method === 'cash')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const cashBalance = cashIncome - cashExpenses;

      // Calculate card totals
      const cardIncome = transactions
        .filter(t => t.type === 'income' && t.payment_method === 'card')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const cardExpenses = transactions
        .filter(t => t.type === 'expense' && t.payment_method === 'card')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const cardBalance = cardIncome - cardExpenses;

      // Calculate total totals
      const totalIncome = cashIncome + cardIncome;
      const totalExpenses = cashExpenses + cardExpenses;
      const totalBalance = totalIncome - totalExpenses;

      return {
        cash: {
          income: cashIncome,
          expenses: cashExpenses,
          balance: cashBalance
        },
        card: {
          income: cardIncome,
          expenses: cardExpenses,
          balance: cardBalance
        },
        total: {
          income: totalIncome,
          expenses: totalExpenses,
          balance: totalBalance
        }
      };
    } catch (err) {
      console.error('Error calculating summary:', err);
      return {
        cash: { income: 0, expenses: 0, balance: 0 },
        card: { income: 0, expenses: 0, balance: 0 },
        total: { income: 0, expenses: 0, balance: 0 }
      };
    }
  };

  return {
    loading,
    error,
    getIncomeTypes,
    getExpenseTypes,
    createIncomeType,
    createExpenseType,
    updateIncomeType,
    updateExpenseType,
    getTransactions,
    addTransaction,
    getSummary
  };
}